/**
 * Helper: Format money
 * -----------------------------------------------------------------------------
 * Extends @shopify/theme-currency with Shopify money placeholders it misses.
 *
 */
import { formatMoney as shopifyFormatMoney } from '@shopify/theme-currency'

/**
 * @param {Number} number - Value in cents.
 * @param {Number} [precision]
 * @param {String} [thousands]
 * @param {String} [decimal]
 * @returns {String}
 */
function formatWithDelimiters(
  number,
  precision = 2,
  thousands = ',',
  decimal = '.',
) {
  if (isNaN(number) || number == null) {
    return '0'
  }

  const value = (number / 100.0).toFixed(precision)
  const parts = value.split('.')
  const dollarsAmount = parts[0].replace(
    /(\d)(?=(\d\d\d)+(?!\d))/g,
    `$1${thousands}`,
  )
  const centsAmount = parts[1] ? decimal + parts[1] : ''

  return dollarsAmount + centsAmount
}

const extendedPlaceholders = {
  amount_with_space_separator: (cents) => formatWithDelimiters(cents, 2, ' ', ','),
  amount_no_decimals_with_space_separator: (cents) => formatWithDelimiters(cents, 0, ' '),
  amount_with_apostrophe_separator: (cents) => formatWithDelimiters(cents, 2, "'", '.'),
  amount_no_decimals_with_apostrophe_separator: (cents) => formatWithDelimiters(cents, 0, "'"),
}

/**
 * Format money values based on shop currency settings.
 * @param {Number|String} cents - Value in cents.
 * @param {String} format - Shop money format string.
 * @returns {String}
 */
export function formatMoney(cents, format) {
  const match = (format || '').match(/\{\{\s*(\w+)\s*\}\}/)
  const placeholder = match?.[1]
  const formatter = extendedPlaceholders[placeholder]

  if (!formatter) {
    return shopifyFormatMoney(cents, format)
  }

  let amount = cents

  if (typeof amount === 'string') {
    amount = amount.replace('.', '')
  }

  return format.replace(match[0], formatter(Number(amount)))
}
